import React, { useState, useRef } from 'react';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebaseConfig';
import Swal from 'sweetalert2';

interface PhotoUploadProps {
  onUpload: (urls: string[]) => void;
  onDelete?: (url: string) => void;
  existingPhotos?: string[];
  maxFiles?: number;
  maxSizeMB?: number;
  allowedTypes?: string[];
  storageFolder: string;
  entityId?: string;
  disabled?: boolean;
  className?: string;
}

interface UploadProgress {
  file: File;
  progress: number;
  url?: string;
  error?: string;
}

export default function PhotoUpload({
  onUpload,
  onDelete,
  existingPhotos = [],
  maxFiles = 10,
  maxSizeMB = 5,
  allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  storageFolder,
  entityId,
  disabled = false,
  className = ''
}: PhotoUploadProps) {
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (!allowedTypes.includes(file.type)) {
      return `Tipo de archivo no permitido. Solo: ${allowedTypes.join(', ')}`;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      return `El archivo es demasiado grande. Máximo: ${maxSizeMB}MB`;
    }

    return null;
  };

  // F5B-006: validar dimensiones antes de procesar (defensa contra imágenes gigantes).
  const MAX_RAW_WIDTH = 12000;
  const MAX_RAW_HEIGHT = 12000;
  const MIN_WIDTH = 300;
  const MIN_HEIGHT = 300;
  const validateDimensions = (file: File): Promise<string | null> => {
    return new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        if (img.width < MIN_WIDTH || img.height < MIN_HEIGHT) {
          resolve(`Imagen muy pequeña (${img.width}x${img.height}). Mínimo ${MIN_WIDTH}x${MIN_HEIGHT}.`);
        } else if (img.width > MAX_RAW_WIDTH || img.height > MAX_RAW_HEIGHT) {
          resolve(`Imagen demasiado grande (${img.width}x${img.height}). Máximo ${MAX_RAW_WIDTH}x${MAX_RAW_HEIGHT}.`);
        } else {
          resolve(null);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve('Archivo no es una imagen válida o está corrupta.');
      };
      img.src = url;
    });
  };

  // F5B-006: canvas re-encode strip EXIF metadata (incluye GPS, autor, cámara).
  const compressImage = (file: File, quality: number = 0.8): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        const maxWidth = 1920;
        const maxHeight = 1080;
        let { width, height } = img;

        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          file.type,
          quality
        );
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const uploadFile = async (file: File): Promise<string> => {
    const timestamp = Date.now();
    const fileName = `${entityId || 'photo'}_${timestamp}_${file.name}`;
    const fileRef = ref(storage, `${storageFolder}/${fileName}`);

    const compressedFile = await compressImage(file);
    const uploadTask = uploadBytesResumable(fileRef, compressedFile);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(prev => 
            prev.map(item => 
              item.file === file ? { ...item, progress } : item
            )
          );
        },
        (error) => {
          console.error('Error uploading file:', error);
          setUploadProgress(prev => 
            prev.map(item => 
              item.file === file ? { ...item, error: error.message } : item
            )
          );
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            setUploadProgress(prev => 
              prev.map(item => 
                item.file === file ? { ...item, url: downloadURL, progress: 100 } : item
              )
            );
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    if (files.length === 0) return;

    if (existingPhotos.length + files.length > maxFiles) {
      Swal.fire({
        icon: 'warning',
        title: 'Límite excedido',
        text: `Máximo ${maxFiles} fotos permitidas. Actualmente tienes ${existingPhotos.length}.`
      });
      return;
    }

    // Validar archivos (tipo + tamaño + dimensiones)
    const validationErrors: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const typeError = validateFile(file);
      if (typeError) {
        validationErrors.push(`Archivo ${i + 1}: ${typeError}`);
        continue;
      }
      const dimError = await validateDimensions(file);
      if (dimError) {
        validationErrors.push(`Archivo ${i + 1}: ${dimError}`);
      }
    }

    if (validationErrors.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Archivos inválidos',
        html: validationErrors.join('<br>')
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(files.map(file => ({ file, progress: 0 })));

    try {
      const uploadPromises = files.map(uploadFile);
      const urls = await Promise.all(uploadPromises);
      
      onUpload(urls);
      
      Swal.fire({
        icon: 'success',
        title: 'Fotos subidas exitosamente',
        text: `${urls.length} foto(s) subida(s) correctamente.`
      });

      setUploadProgress([]);
    } catch (error) {
      console.error('Error uploading files:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al subir fotos',
        text: 'Hubo un problema subiendo algunas fotos. Inténtalo de nuevo.'
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (photoUrl: string) => {
    const confirm = await Swal.fire({
      title: '¿Eliminar foto?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33'
    });

    if (!confirm.isConfirmed) return;

    try {
      const photoRef = ref(storage, photoUrl);
      await deleteObject(photoRef);
      
      if (onDelete) {
        onDelete(photoUrl);
      }

      Swal.fire({
        icon: 'success',
        title: 'Foto eliminada',
        text: 'La foto ha sido eliminada correctamente.'
      });
    } catch (error) {
      console.error('Error deleting photo:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo eliminar la foto. Inténtalo de nuevo.'
      });
    }
  };

  return (
    <div className={`photo-upload ${className}`}>
      {/* Input de archivos */}
      <div className="mb-3">
        <label className="form-label">
          <i className="fa fa-camera me-2"></i>
          Fotos ({existingPhotos.length}/{maxFiles})
        </label>
        <input
          ref={fileInputRef}
          type="file"
          className="form-control"
          accept={allowedTypes.join(',')}
          multiple
          onChange={handleFileSelect}
          disabled={disabled || isUploading}
        />
        <div className="form-text">
          Máximo {maxFiles} fotos. Tamaño máximo: {maxSizeMB}MB por foto.
          <br />Formatos permitidos: JPEG, JPG, PNG, WebP
        </div>
      </div>

      {/* Progreso de subida */}
      {uploadProgress.length > 0 && (
        <div className="mb-3">
          <h6>Subiendo fotos...</h6>
          {uploadProgress.map((item, index) => (
            <div key={index} className="mb-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small>{item.file.name}</small>
                <small>{Math.round(item.progress)}%</small>
              </div>
              <div className="progress" style={{ height: '4px' }}>
                <div
                  className={`progress-bar ${item.error ? 'bg-danger' : 'bg-success'}`}
                  role="progressbar"
                  style={{ width: `${item.progress}%` }}
                ></div>
              </div>
              {item.error && (
                <small className="text-danger">{item.error}</small>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Galería de fotos existentes */}
      {existingPhotos.length > 0 && (
        <div className="photo-gallery">
          <h6>Fotos actuales:</h6>
          <div className="row">
            {existingPhotos.map((photoUrl, index) => (
              <div key={index} className="col-md-3 col-sm-4 col-6 mb-3">
                <div className="position-relative">
                  <img
                    src={photoUrl}
                    alt={`Foto ${index + 1}`}
                    className="img-fluid rounded shadow-sm"
                    style={{ aspectRatio: '1', objectFit: 'cover', width: '100%' }}
                  />
                  {!disabled && (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1"
                      onClick={() => handleDeletePhoto(photoUrl)}
                      style={{ fontSize: '12px' }}
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}